import { type Allergen, type MenuChannel } from '@owner/menu-entities'

import {
  type BooleanFilterTranslation,
  type DropdownFilterTranslation,
} from '@/components/filtering/filter-set/types'

import { type ResourceTypes } from '../types'

interface InputTranslation {
  label: string
  description?: string
  placeholder?: string
}

export interface MenuTranslations {
  actions: Record<string, string>
  allergen: Record<Allergen, string>
  channel: Record<MenuChannel, InputTranslation>
  errors: Record<string, string>
  filters: Record<string, BooleanFilterTranslation | DropdownFilterTranslation>
  inputs: Record<string, InputTranslation>
  labels: Record<string, string>
  notices: Record<string, string>
  notifications: Record<string, string>
  resources: Record<ResourceTypes, string>
  selectors: Record<string, string>
  tooltips: Record<string, string>
}

export const menuEn: MenuTranslations = {
  actions: {
    'restore-from-pos': 'Restore from POS',
    restoring: 'Restoring...',
    retry: 'Try Again',
    'undo-split': 'Undo Split',
  },
  allergen: {
    dairy: 'Dairy',
    eggs: 'Eggs',
    fish: 'Fish',
    gluten: 'Gluten',
    mango: 'Mango',
    peanuts: 'Peanuts',
    sesame: 'Sesame',
    shellfish: 'Shellfish',
    soy: 'Soy',
    'tree-nuts': 'Tree Nuts',
  },
  channel: {
    kiosk: {
      description: 'Your Owner.com self-service kiosk',
      label: 'Kiosk',
    },
    online: {
      description: 'Your Owner.com website and app',
      label: 'Online',
    },
    pos: {
      description: 'Your Owner.com point of sale',
      label: 'POS',
    },
  },
  errors: {
    caloriesMaxGreaterThanMin:
      'Maximum calories must be greater than minimum calories',
    caloriesMaxRequiresMin: 'Must set a minimum when maximum is set',
    caloriesMinLessThanMax:
      'Minimum calories must be less than maximum calories',
    caloriesNonNegative: 'Calories must be a non-negative number',
    duplicateCouponCode:
      'This coupon code is already in use. Please choose a different code or delete the existing coupon first.',
    fetchFailed: 'Unable to load {resourceName}.',
    fetchFailedDescription:
      'We were unable to find this {resourceName}. It may have been deleted or merged with another.',
    fetchFailedTitle: '{resourceName} not found',
    notFound:
      "This {resourceName} no longer exists or you don't have access to it.",
    save: 'Unable to save {resourceName}.',
  },
  filters: {
    alcohol: {
      label: 'Alcohol',
      options: {
        false: 'No Alcohol',
        true: 'Alcohol',
      },
    },
    allowInstructions: {
      label: 'Allow Instructions',
      options: {
        false: 'No Instructions',
        true: 'Instructions',
      },
    },
    available: {
      label: 'Availability',
      options: {
        false: 'Not Available',
        true: 'Available',
      },
    },
    campaign: {
      label: 'Associated with automation',
      options: {
        basic: 'Basic',
        welcome: 'Welcome',
        winback: 'Winback',
      },
    },
    glutenFree: {
      label: 'Gluten Free',
      options: {
        false: 'Not Gluten Free',
        true: 'Gluten Free',
      },
    },
    hasCategories: {
      label: 'Categories',
      options: {
        false: 'No Categories',
        true: 'Has Categories',
      },
    },
    hasItems: {
      label: 'Items',
      options: {
        false: 'No Items',
        true: 'Has Items',
      },
    },
    hasModifierSets: {
      label: 'Option groups',
      options: {
        false: 'No option groups',
        true: 'Has option groups',
      },
    },
    hasModifiers: {
      label: 'Options',
      options: {
        false: 'No options',
        true: 'Has options',
      },
    },
    hasPreselectedModifiers: {
      label: 'Preselected options',
      options: {
        false: 'No preselected options',
        true: 'Has preselected options',
      },
    },
    hasUpsellCategories: {
      label: 'Upsell Categories',
      options: {
        false: 'No Upsell Categories',
        true: 'Has Upsell Categories',
      },
    },
    linkedToPos: {
      label: 'Linked to POS',
      options: {
        false: 'Not linked',
        true: 'Linked',
      },
    },
    popular: {
      label: 'Popular',
      options: {
        false: 'Not Popular',
        true: 'Popular',
      },
    },
    promotional: {
      label: 'Promotional',
      options: {
        false: 'Not Promotional',
        true: 'Promotional',
      },
    },
    taxable: {
      label: 'Tax',
      options: {
        false: 'Not Taxable',
        true: 'Taxable',
      },
    },
    topping: {
      label: 'Topping',
      options: {
        false: 'Not Topping',
        true: 'Topping',
      },
    },
    toppings: {
      label: 'Toppings',
      options: {
        false: 'Not Toppings',
        true: 'Toppings',
      },
    },
    type: {
      label: 'Menu Type',
      options: {
        catering: 'Catering',
        olo: 'Online Ordering',
      },
    },
    upsell: {
      label: 'Upsell',
      options: {
        false: 'Not Upsell Item',
        true: 'Is Upsell Item',
      },
    },
    vegetarian: {
      label: 'Vegetarian',
      options: {
        false: 'Not Vegetarian',
        true: 'Vegetarian',
      },
    },
  },
  inputs: {
    alcohol: {
      label: 'Alcohol',
    },
    allergens: {
      label: 'Allergens',
    },
    allowInstructions: {
      description:
        'Customers will be able to add comments to items when placing an order (eg. ‘no onions’).',
      label: 'Allow Special Instructions',
    },
    amount: {
      label: 'Amount',
    },
    available: {
      description: 'Only available items will show on your menu.',
      label: 'Available',
    },
    availableModifierSets: {
      description: 'Only available option groups will show on your menu.',
      label: 'Available',
    },
    categories: {
      description:
        'Categories contain a list of items (eg. Appetizers, Entrees, Desserts).',
      label: 'Categories',
    },
    channels: {
      description: 'Choose where this {resourceName} shows up.',
      label: 'Channels',
    },
    description: {
      label: 'Description',
    },
    'description-override': {
      label: 'Description override',
      placeholder: 'Description override',
    },
    displayName: {
      description: 'This is the name your customers will see.',
      label: 'Display name',
    },
    'exclude-from-overrides': {
      description:
        'Exclude this {resourceName} from being counted in order throttling and large orders.',
      label: 'Exclude from overrides',
    },
    glutenFree: {
      label: 'Gluten Free',
    },
    groupName: {
      label: 'Group name',
    },
    'hide-from-guests': {
      label: 'Hide from guest menu',
    },
    image: {
      label: 'Photo',
    },
    'image-override': {
      label: 'Photo override',
    },
    items: {
      description:
        'The items on your menu (eg. Pizza, Burger, Chocolate Cake).',
      label: 'Items',
    },
    maximumCalories: {
      label: 'Maximum Calories',
      placeholder: 'e.g., 300',
    },
    menuAvailability: {
      description:
        'The days and hours the menu is available for your customers.',
      label: 'Availability Hours',
    },
    minimumCalories: {
      label: 'Minimum Calories',
      placeholder: 'e.g., 250',
    },
    modifierSelection: {
      description:
        'The minimum and maximum quantity of this option that a customer must select when ordering.',
      label: 'Selection',
    },
    modifierSetSelection: {
      description:
        'The minimum number of options a customer must select from this option group when ordering and the maximum number of options they can select.',
      label: 'Selection',
    },
    modifierSets: {
      description:
        'Options allow customers to customize an item. You might have a option group like “Toppings”.',
      label: 'Option groups',
    },
    modifierSetsNested: {
      description: 'Nested options allow customers to customize an option.',
      label: 'Nested option groups',
    },
    modifiers: {
      description:
        'Options allow customers to customize an item (eg. Pepperoni, No Onions, Sprinkles).',
      label: 'Options',
    },
    name: {
      label: 'Internal Name',
    },
    'name-override': {
      label: 'Name override',
      placeholder: 'Name override',
    },
    optionSelection: {
      description: 'Choose how guests can select options.',
      label: 'Option selection',
    },
    options: {
      label: 'Options',
    },
    'override-description': {
      label: 'Override description',
    },
    'override-image': {
      label: 'Override photo',
    },
    'override-name': {
      label: 'Override name',
    },
    'override-ordering': {
      label: 'Override {entityName} Order',
    },
    points: {
      label: 'Points to Redeem',
      placeholder: 'Not Redeemable',
    },
    popular: {
      description:
        'Popular items are highlighted on your website and marketing.',
      label: 'Popular Item',
    },
    'pos-name': {
      label: 'Name from POS',
    },
    taxable: {
      label: 'Apply sales tax for this item',
    },
    toppings: {
      label: 'The options in this group are item toppings',
    },
    upsell: {
      description:
        'Upsell items will be promoted to your guests as they order.',
      label: 'Upsell Item',
    },
    upsellCategories: {
      description:
        'These categories are upsold to your customers when they order items from this category.',
      label: 'Upsell Categories',
    },
    variationGroups: {
      description:
        'Variations are different versions of an item. You might have a variation group like “Size” or “Flavor”.',
      label: 'Variation Groups',
    },
    vegetarian: {
      label: 'Vegetarian',
    },
  },
  labels: {
    'calorie-count': 'Calorie Count',
    'calorie-count-description':
      'Specify the minimum and maximum number of calories.',
    'consolidated-from': 'Consolidated from',
    'custom-internal': 'Use a different name internally',
    'custom-internal-description':
      'This is the name you and your staff will see on the dashboard, tablet and printed tickets.',
    delete: 'Delete',
    'delete-orphaned': 'Delete Orphaned Resource',
    'delete-orphaned-confirm-message':
      'Are you sure you want to delete this orphaned resource?',
    'delete-orphaned-confirm-title': 'Delete Orphaned Resource',
    'display-type-custom': 'Mixed',
    'display-type-custom-description': 'Various option selections',
    'display-type-multiple': 'Multiple',
    'display-type-multiple-description': 'Select more than one',
    'display-type-quantity': 'Quantity',
    'display-type-quantity-description': 'Select the amount',
    'display-type-single': 'Single',
    'display-type-single-description': 'Select one option',
    'display-type-topping': 'Pizza',
    'display-type-topping-description': 'Select half or whole',
    'guest-display': 'Guest Display',
    hidden: 'Hidden',
    'merged-items': 'Merged items',
    'merged-items-description':
      'The items below were merged into this item for online ordering.',
    'more-options': 'More settings',
    'no-parents':
      'This is not currently used anywhere in your menu. Consider deleting if it is no longer needed.',
    options: 'Options',
    'parent-categories': 'Parent Categories',
    'parent-categories-description':
      'This item appears in the following categories.',
    'parent-items': 'Used in these items',
    'parent-items-description':
      'This option group appears in the following items.',
    'parent-modifier-sets': 'Parent option groups',
    'parent-modifier-sets-description':
      'This option appears in the following option groups.',
    'parent-modifiers': 'Parent options',
    'parent-modifiers-description':
      'This option group appears in the following options.',
    'pos-availability': 'POS Availability',
    'pos-availability-description':
      'This item is currently {status} in your POS system.',
    'pos-available': 'Available',
    'pos-unavailable': 'Unavailable',
    promotion: 'Promotion',
    'resource-available': '{resourceName} is available for ordering.',
    'resource-created': '{resourceName} Created',
    'resource-unavailable': '{resourceName} is not available for ordering.',
    'resource-updated': '{resourceName} Updated',
    'selection-maximum': 'Maximum',
    'selection-minimum': 'Minimum',
    'selection-unlimited': 'No Limit',
    'spawned-description-and': 'and',
    'spawned-description-prefix': 'This POS option group was split into',
    'spawned-modifier-set': 'Split option group',
    'split-description-from-pos':
      'group from the POS was split into this option group and',
    'split-description-guest-clarity': 'for guest clarity',
    'split-description-the': 'The',
    'split-modifier-set': 'Split option group',
    'virtual-parent-categories': 'Added to Categories',
    'virtual-parent-categories-description':
      'This item has been added to these categories for online ordering.',
    'virtual-parent-items': 'Added to Items',
    'virtual-parent-items-description':
      'This option group has been added to these items for online ordering.',
    'virtual-parent-modifier-sets': 'Added to Option Groups',
    'virtual-parent-modifier-sets-description':
      'This option has been added to these option groups for online ordering.',
    'virtual-parent-modifiers': 'Added to Options',
    'virtual-parent-modifiers-description':
      'This option group has been added to these options for online ordering.',
  },
  notices: {
    'delete-orphaned-error': 'Unable to delete orphaned resource',
    'delete-orphaned-success': 'Successfully deleted orphaned resource',
    'pos-connected':
      'Connected to your POS menu. Most changes must be made directly on that platform and only a subset of updates can be made here.',
  },
  notifications: {
    'restore-from-pos-error': 'Failed to restore items from POS',
    'restore-from-pos-success': 'Successfully restored items from POS',
    'restore-split-error': 'Failed to restore option group from POS',
    'restore-split-success': 'Successfully restored option group from POS',
  },
  resources: {
    category: 'Category | Categories',
    coupon: 'Coupon | Coupons',
    item: 'Item | Items',
    menu: 'Menu | Menus',
    modifier: 'Option | Options',
    'modifier-set': 'Option group | Option groups',
  },
  selectors: {
    loading: 'Loading...',
  },
  tooltips: {
    delete: 'Delete',
    duplicate: 'Duplicate',
    hide: 'Hide',
    show: 'Show',
  },
}
